import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAY_LABELS = ["S", "T", "Q", "Q", "S", "S", "D"];
const MONTH_NAMES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const JourneyCalendar = ({ schedule, selected, onSelect }) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(
    () => new Date(today.getTime() + (schedule?.max_days_ahead ?? 60) * 86400000),
    [today, schedule]
  );
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const cells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstWeekdayMondayFirst = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const list = Array.from({ length: firstWeekdayMondayFirst }, () => null);
    for (let day = 1; day <= daysInMonth; day += 1) list.push(new Date(year, month, day));
    return list;
  }, [cursor]);

  const canPrev = new Date(cursor.getFullYear(), cursor.getMonth(), 1) > new Date(today.getFullYear(), today.getMonth(), 1);
  const canNext = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const isAllowed = (d) => {
    if (d < today || d > maxDate) return false;
    const weekdayMondayFirst = (d.getDay() + 6) % 7;
    return (schedule?.days?.[String(weekdayMondayFirst)] || []).length > 0;
  };

  return (
    <div data-testid="journey-calendar" className="rounded-3xl border border-raiz-900/10 bg-paper p-5 sm:p-7">
      <div className="flex items-center justify-between">
        <p className="font-serif text-xl capitalize text-ink" data-testid="calendar-month-label">
          {MONTH_NAMES[cursor.getMonth()]} <span className="text-ink/50">{cursor.getFullYear()}</span>
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="calendar-prev-button"
            aria-label="Mês anterior"
            disabled={!canPrev}
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-raiz-800/20 text-raiz-800 transition-colors duration-300 hover:border-raiz-800 disabled:opacity-30 disabled:hover:border-raiz-800/20"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="calendar-next-button"
            aria-label="Próximo mês"
            disabled={!canNext}
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-raiz-800/20 text-raiz-800 transition-colors duration-300 hover:border-raiz-800 disabled:opacity-30 disabled:hover:border-raiz-800/20"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-7 gap-1.5 text-center">
        {WEEKDAY_LABELS.map((w, i) => (
          <span key={`${w}-${i}`} className="pb-2 text-[11px] font-medium uppercase tracking-widest text-ink/40">
            {w}
          </span>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <span key={`empty-${i}`} />
          ) : (
            <button
              key={toISO(d)}
              type="button"
              data-testid={`calendar-day-${toISO(d)}`}
              disabled={!isAllowed(d)}
              onClick={() => onSelect(toISO(d))}
              className={`aspect-square rounded-full text-sm transition-colors duration-300 ${
                selected === toISO(d)
                  ? "bg-raiz-800 font-medium text-paper"
                  : isAllowed(d)
                    ? "text-ink hover:bg-gold-100 hover:text-raiz-900"
                    : "cursor-default text-ink/25"
              }`}
            >
              {d.getDate()}
            </button>
          )
        )}
      </div>
      <p className="mt-5 text-xs leading-relaxed text-ink/50">Atendimentos de segunda a sexta. Escolha um dia disponível.</p>
    </div>
  );
};

export default JourneyCalendar;
